import { Schema } from "effect";
import {
  OfficeArtifactKindSchema,
  OfficeCanonicalIdSchema,
  OfficePublicErrorSchema,
} from "./office";
import {
  ProjectFileContentTypeSchema,
  ProjectFileNameSchema,
  ProjectFilePrivateObjectKeySchema,
  ProjectFileRevisionIdSchema,
  ProjectFileSha256Schema,
  ProjectFileStorageObjectIdSchema,
  ProjectFileHttpUrlSchema,
  ProjectFileIsoDateTimeSchema,
  ProjectFilePositiveIntegerSchema,
  ProjectFileActorUserIdSchema,
} from "./project-files";
import {
  SandboxProjectIdSchema,
  SandboxRequestIdSchema,
  SandboxWorkspaceIdSchema,
} from "./sandbox";

export const OfficeContentBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const OFFICE_BROWSER_REVISION_MAX_BYTES = 50 * 1_048_576;

export const OfficeArtifactIdSchema = OfficeCanonicalIdSchema.pipe(
  Schema.brand("OfficeArtifactId"),
);
export const OfficeArtifactRevisionIdSchema = ProjectFileRevisionIdSchema.pipe(
  Schema.brand("OfficeArtifactRevisionId"),
);
export const OfficeStorageObjectIdSchema = ProjectFileStorageObjectIdSchema.pipe(
  Schema.brand("OfficeStorageObjectId"),
);

export const CreateOfficeRevisionDownloadTicketRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  artifactId: OfficeArtifactIdSchema,
  revisionId: Schema.optionalKey(OfficeArtifactRevisionIdSchema),
});

export const OfficeRevisionDownloadTicketSchema = Schema.Struct({
  artifactId: OfficeArtifactIdSchema,
  revisionId: OfficeArtifactRevisionIdSchema,
  kind: OfficeArtifactKindSchema,
  name: ProjectFileNameSchema,
  contentType: ProjectFileContentTypeSchema,
  byteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(OFFICE_BROWSER_REVISION_MAX_BYTES),
  ),
  sha256: ProjectFileSha256Schema,
  url: ProjectFileHttpUrlSchema,
  expiresAt: ProjectFileIsoDateTimeSchema,
});

export const OfficeRepositoryResolveRevisionRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: ProjectFileActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  artifactId: OfficeArtifactIdSchema,
  revisionId: Schema.optionalKey(OfficeArtifactRevisionIdSchema),
});

export const OfficeRepositoryResolvedRevisionSchema = Schema.Struct({
  artifactId: OfficeArtifactIdSchema,
  revisionId: OfficeArtifactRevisionIdSchema,
  storageObjectId: OfficeStorageObjectIdSchema,
  kind: OfficeArtifactKindSchema,
  name: ProjectFileNameSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  contentType: ProjectFileContentTypeSchema,
  storageContentType: Schema.Union([Schema.Literal(""), ProjectFileContentTypeSchema]),
  byteSize: ProjectFilePositiveIntegerSchema,
  sha256: ProjectFileSha256Schema,
});

export const OfficeContentPublicErrorSchema = OfficePublicErrorSchema;

export type CreateOfficeRevisionDownloadTicketRequest =
  typeof CreateOfficeRevisionDownloadTicketRequestSchema.Type;
export type OfficeRevisionDownloadTicket = typeof OfficeRevisionDownloadTicketSchema.Type;
export type OfficeRepositoryResolveRevisionRequest =
  typeof OfficeRepositoryResolveRevisionRequestSchema.Type;
export type OfficeRepositoryResolvedRevision = typeof OfficeRepositoryResolvedRevisionSchema.Type;

export const decodeCreateOfficeRevisionDownloadTicketRequest = Schema.decodeUnknownSync(
  CreateOfficeRevisionDownloadTicketRequestSchema,
  OfficeContentBoundaryParseOptions,
);
export const decodeOfficeRevisionDownloadTicket = Schema.decodeUnknownSync(
  OfficeRevisionDownloadTicketSchema,
  OfficeContentBoundaryParseOptions,
);
export const decodeOfficeRepositoryResolveRevisionRequest = Schema.decodeUnknownSync(
  OfficeRepositoryResolveRevisionRequestSchema,
  OfficeContentBoundaryParseOptions,
);
export const decodeOfficeRepositoryResolvedRevision = Schema.decodeUnknownSync(
  OfficeRepositoryResolvedRevisionSchema,
  OfficeContentBoundaryParseOptions,
);
export const decodeOfficeContentPublicError = Schema.decodeUnknownSync(
  OfficeContentPublicErrorSchema,
  OfficeContentBoundaryParseOptions,
);
